import type { ShopCapability, StoreCapabilities, StoreRole } from "@hobbyshopify/backend-client";
import { can } from "./store-role";

type CapabilityFlag = "pointOfSale" | "liveBreaks" | "hostEvents";

const FLAG: Record<ShopCapability, CapabilityFlag> = {
  POINT_OF_SALE: "pointOfSale",
  LIVE_BREAKS: "liveBreaks",
  HOST_EVENTS: "hostEvents",
};

export const CAPABILITY_LABEL: Record<ShopCapability, string> = {
  POINT_OF_SALE: "Point of Sale",
  LIVE_BREAKS: "Live breaks",
  HOST_EVENTS: "Hosting shows",
};

export const CAPABILITY_PITCH: Record<ShopCapability, string> = {
  POINT_OF_SALE:
    "Customer scans and carts, scan & review, taking payment at the counter and the Shopify POS app. HobbyScan switches it on per shop.",
  LIVE_BREAKS: "Live streams, breaks, spot lists and the live queue. HobbyScan switches it on per shop.",
  HOST_EVENTS: "Host your own card shows and sell their tables. HobbyScan switches it on per shop.",
};

/** Whether the shop has `capability`; null while the answer is not in. */
export function hasCapability(capabilities: StoreCapabilities | null, capability: ShopCapability): boolean | null {
  return capabilities ? capabilities[FLAG[capability]] : null;
}

/**
 * Where the signed-in member stands with a capability: on, already asked for,
 * theirs to ask for (a Manager or an Owner), or for the shop owner to ask.
 * `unknown` while the shop or its capabilities have not resolved.
 */
export type CapabilityAccess = "unknown" | "on" | "requested" | "requestable" | "askOwner";

export function capabilityAccess(
  capabilities: StoreCapabilities | null,
  capability: ShopCapability,
  role: StoreRole | null,
): CapabilityAccess {
  if (!capabilities || !role) return "unknown";
  if (capabilities[FLAG[capability]]) return "on";
  if (capabilities.requested.includes(capability)) return "requested";
  return can(role, "requestCapabilities") ? "requestable" : "askOwner";
}

export const CONTACT_EMAIL_REQUIRED = "Add an email address HobbyScan can reach you at.";

/** Why a request cannot go yet: no address typed and none on file. */
export function contactEmailProblem(draft: string, onFile: string | null): string | null {
  return draft.trim() || onFile ? null : CONTACT_EMAIL_REQUIRED;
}
